/**
 * React Query hooks for the safety register, safety reports, security
 * incidents and anomaly acknowledgements.
 *
 * Reads take an `enabled` flag because they are asked for from the shell:
 * the feed that fills the old boards turns them on only once a board's
 * engine has loaded, and only for a role that holds the permission — a
 * viewer is not sent round a 403 every minute.
 *
 * Every write refreshes the safety reads, and the KPIs (the open-incident
 * figure moves with the security register).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "../app/SessionContext";
import {
  ANOMALY_ACK_TOPIC,
  type AssessHazardInput,
  type CloseHazardInput,
  type FileReportInput,
  type HazardActionInput,
  type RaiseHazardInput,
  type ReportStatus,
} from "../domain/safety";

export const safetyKeys = {
  vocabulary: () => ["safety-vocabulary"] as const,
  hazards: (siteId: string) => ["safety-hazards", siteId] as const,
  hazard: (id: string) => ["hazard", id] as const,
  reports: (siteId: string) => ["safety-reports", siteId] as const,
  report: (id: string) => ["safety-report", id] as const,
  securityVocabulary: () => ["security-vocabulary"] as const,
  incidents: (siteId: string) => ["security-incidents", siteId] as const,
  /* the same key `useNotes` uses, so one fetch serves both */
  acks: (siteId: string) => ["notes", siteId, ANOMALY_ACK_TOPIC] as const,
};

/** What a safety write changes, for `invalidateQueries`. */
export const SAFETY_WRITE_KEYS: readonly (readonly string[])[] = [
  ["safety-hazards"],
  ["hazard"],
  ["safety-reports"],
  ["safety-report"],
  ["security-incidents"],
  ["kpis"],
];

interface ReadOpts {
  enabled?: boolean;
}

/* a colleague's entry arrives with LiveRefresh's event poll; this is the backstop */
const poll = (live: boolean) => (live ? 60_000 : false);

export function useSafetyVocabulary(opts: ReadOpts = {}) {
  const { source } = useSession();
  return useQuery({
    queryKey: safetyKeys.vocabulary(),
    queryFn: () => source.safetyVocabulary(),
    staleTime: 10 * 60_000,
    enabled: opts.enabled ?? true,
    retry: false,
  });
}

export function useHazards(opts: ReadOpts = {}) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: safetyKeys.hazards(site.id),
    queryFn: () => source.listHazards(site.id),
    staleTime: 30_000,
    refetchInterval: poll(source.kind === "platform"),
    enabled: opts.enabled ?? true,
    retry: false,
  });
}

export function useHazard(id: string | null) {
  const { source } = useSession();
  return useQuery({
    queryKey: safetyKeys.hazard(id ?? ""),
    queryFn: () => source.getHazard(id!),
    enabled: !!id,
    retry: false,
  });
}

export function useSafetyReports(opts: ReadOpts = {}) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: safetyKeys.reports(site.id),
    queryFn: () => source.listSafetyReports(site.id),
    staleTime: 30_000,
    refetchInterval: poll(source.kind === "platform"),
    enabled: opts.enabled ?? true,
    retry: false,
  });
}

export function useSafetyReport(id: string | null, opts: ReadOpts = {}) {
  const { source } = useSession();
  return useQuery({
    queryKey: safetyKeys.report(id ?? ""),
    queryFn: () => source.getSafetyReport(id!),
    enabled: !!id && (opts.enabled ?? true),
    retry: false,
  });
}

export function useSecurityVocabulary(opts: ReadOpts = {}) {
  const { source } = useSession();
  return useQuery({
    queryKey: safetyKeys.securityVocabulary(),
    queryFn: () => source.securityVocabulary(),
    staleTime: 10 * 60_000,
    enabled: opts.enabled ?? true,
    retry: false,
  });
}

export function useSecurityIncidents(opts: ReadOpts = {}) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: safetyKeys.incidents(site.id),
    queryFn: () => source.listSecurityIncidents(site.id),
    staleTime: 30_000,
    refetchInterval: poll(source.kind === "platform"),
    enabled: opts.enabled ?? true,
    retry: false,
  });
}

/** Anomaly acknowledgements: site notes under one topic (notes emit no event, hence the poll). */
export function useAnomalyAcks(opts: ReadOpts = {}) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: safetyKeys.acks(site.id),
    queryFn: () => source.listNotes(site.id, ANOMALY_ACK_TOPIC),
    staleTime: 30_000,
    refetchInterval: poll(source.kind === "platform"),
    enabled: opts.enabled ?? true,
    retry: false,
  });
}

/* ── writes ─────────────────────────────────────────────────────────────── */

function useSafetyWrite<I, O>(fn: (input: I) => Promise<O>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const key of SAFETY_WRITE_KEYS) void qc.invalidateQueries({ queryKey: [...key] });
    },
  });
}

export function useFileSafetyReport() {
  const { source } = useSession();
  return useSafetyWrite(({ input, key }: { input: FileReportInput; key?: string }) => source.fileSafetyReport(input, key));
}

export function useRaiseHazard() {
  const { source } = useSession();
  return useSafetyWrite(({ input, key }: { input: RaiseHazardInput; key?: string }) => source.raiseHazard(input, key));
}

export function useAssessHazard() {
  const { source } = useSession();
  return useSafetyWrite(({ id, input }: { id: string; input: AssessHazardInput }) => source.assessHazard(id, input));
}

export function useAddHazardAction() {
  const { source } = useSession();
  return useSafetyWrite(({ id, input }: { id: string; input: HazardActionInput }) => source.addHazardAction(id, input));
}

export function useCompleteHazardAction() {
  const { source } = useSession();
  return useSafetyWrite(({ id, seq, status }: { id: string; seq: number; status: "done" | "cancelled" }) =>
    source.completeHazardAction(id, seq, status),
  );
}

export function useAcceptHazardRisk() {
  const { source } = useSession();
  return useSafetyWrite(({ id, rationale, reviewDue }: { id: string; rationale: string; reviewDue?: string }) =>
    source.acceptHazardRisk(id, { rationale, reviewDue }),
  );
}

export function useCloseHazard() {
  const { source } = useSession();
  return useSafetyWrite(({ id, input }: { id: string; input: CloseHazardInput }) => source.closeHazard(id, input));
}

export function useSetReportStatus() {
  const { source } = useSession();
  return useSafetyWrite(({ id, status, outcome }: { id: string; status: ReportStatus; outcome?: string }) =>
    source.setSafetyReportStatus(id, { status, outcome }),
  );
}

export function useLinkReport() {
  const { source } = useSession();
  return useSafetyWrite(({ id, hazardId }: { id: string; hazardId: string | null }) => source.linkSafetyReport(id, hazardId));
}

export function useRaiseHazardFromReport() {
  const { source } = useSession();
  return useSafetyWrite(
    ({ id, input }: { id: string; input: { category: string; title: string; description?: string } }) =>
      source.raiseHazardFromReport(id, input),
  );
}
